import { forwardRef } from 'react'
import { cva } from 'class-variance-authority'
import cn from 'clsx'
import { Spinner } from '../spinner'
import type { TButtonProps } from './Button.types'
import styles from './Button.module.css'

/** Exported for anchors that need the button look — the DS has no button-shaped link. */
export const buttonClassNames = cva(styles.button, {
    variants: {
        variant: {
            primary: styles['variant-primary'],
            secondary: styles['variant-secondary'],
            tertiary: styles['variant-tertiary'],
            embed: styles['variant-embed'],
        },
        size: {
            tiny: styles['size-tiny'],
            small: styles['size-small'],
            experiment: styles['size-experiment'],
            medium: styles['size-medium'],
            large: styles['size-large'],
            embed: styles['size-embed'],
        },
    },
    defaultVariants: { variant: 'primary', size: 'large' },
})

export const Button = forwardRef<HTMLButtonElement, TButtonProps>(
    (
        {
            className,
            variant = 'primary',
            size = 'large',
            children,
            LeftIcon,
            loading = false,
            disabled: disabledProp,
            hideMobileText,
            ...props
        },
        ref,
    ) => {
        const disabled = disabledProp || loading

        const childrenClasses = cn(styles.label, { [styles.labelHiddenMobile]: hideMobileText })

        return (
            <button
                ref={ref}
                type="button"
                className={cn(buttonClassNames({ variant, size }), className)}
                disabled={disabled}
                {...props}
            >
                {loading ? (
                    <span className={styles.loading}>
                        <Spinner className={styles.spinner} />
                        {children ? <span className={childrenClasses}>{children}</span> : null}
                    </span>
                ) : (
                    <>
                        {LeftIcon ? <LeftIcon className={styles.icon} /> : null}
                        {children ? <span className={childrenClasses}>{children}</span> : null}
                    </>
                )}
            </button>
        )
    },
)
